import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { FileText, Upload, Trash2, AlertTriangle } from "lucide-react";
import type { FolioAuthorization } from "../types";
import { DTE_TYPE_NAMES } from "../types";
import { CafUploadDialog } from "./CafUploadDialog";

interface FolioListProps {
  folios: FolioAuthorization[];
  isLoading: boolean;
  onUpload: (cafXml: string) => Promise<void>;
  isUploading: boolean;
  onDeactivate: (id: string) => Promise<void>;
}

/**
 * Component to display and manage folio authorizations (CAFs).
 */
export function FolioList({
  folios,
  isLoading,
  onUpload,
  isUploading,
  onDeactivate,
}: FolioListProps) {
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedFolioId, setSelectedFolioId] = useState<string | null>(null);

  const activeFolios = folios.filter((f) => f.isActive);

  const handleUploadComplete = async (cafXml: string) => {
    await onUpload(cafXml);
    setUploadDialogOpen(false);
  };

  const handleDeactivateClick = (id: string) => {
    setSelectedFolioId(id);
    setDeleteDialogOpen(true);
  };

  const handleDeactivateConfirm = async () => {
    if (selectedFolioId) {
      await onDeactivate(selectedFolioId);
      setDeleteDialogOpen(false);
      setSelectedFolioId(null);
    }
  };

  const getUsagePercentage = (folio: FolioAuthorization) => {
    const total = folio.folioEnd - folio.folioStart + 1;
    const used = folio.folioCurrent - folio.folioStart;
    return Math.round((used / total) * 100);
  };

  const getStatusColor = (percentage: number) => {
    if (percentage >= 90) return "bg-red-500";
    if (percentage >= 70) return "bg-yellow-500";
    return "bg-green-500";
  };

  return (
    <div className="space-y-6">
      {/* Header with Upload Button */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-medium">Folios Autorizados</h3>
          <p className="text-sm text-muted-foreground">
            Gestione sus autorizaciones de folios (CAF) para cada tipo de documento
          </p>
        </div>
        <Button onClick={() => setUploadDialogOpen(true)}>
          <Upload className="mr-2 h-4 w-4" />
          Cargar CAF
        </Button>
      </div>

      {/* Folio Cards */}
      {isLoading ? (
        <div className="text-center py-8 text-muted-foreground">
          Cargando folios...
        </div>
      ) : activeFolios.length === 0 ? (
        <Card>
          <CardContent className="py-8 text-center">
            <FileText className="mx-auto h-12 w-12 text-muted-foreground" />
            <h3 className="mt-4 text-lg font-medium">Sin folios</h3>
            <p className="mt-2 text-muted-foreground">
              No tiene folios autorizados. Cargue un archivo CAF para comenzar.
            </p>
            <Button
              className="mt-4"
              onClick={() => setUploadDialogOpen(true)}
            >
              <Upload className="mr-2 h-4 w-4" />
              Cargar CAF
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4 md:grid-cols-2">
          {activeFolios.map((folio) => {
            const usagePercentage = getUsagePercentage(folio);
            const isLow = folio.foliosRemaining < 50;

            return (
              <Card key={folio.id}>
                <CardHeader className="pb-2">
                  <div className="flex items-start justify-between">
                    <div>
                      <CardTitle className="text-base">
                        {DTE_TYPE_NAMES[folio.documentTypeCode] ?? `Tipo ${folio.documentTypeCode}`}
                      </CardTitle>
                      <CardDescription>
                        Rango: {folio.folioStart} - {folio.folioEnd}
                      </CardDescription>
                    </div>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="text-destructive hover:text-destructive"
                      onClick={() => handleDeactivateClick(folio.id)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  {/* Progress Bar */}
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span>Usado: {usagePercentage}%</span>
                      <span
                        className={isLow ? "text-destructive font-medium" : ""}
                      >
                        {folio.foliosRemaining} restantes
                      </span>
                    </div>
                    <div className="h-2 bg-muted rounded-full overflow-hidden">
                      <div
                        className={`h-full ${getStatusColor(usagePercentage)}`}
                        style={{ width: `${usagePercentage}%` }}
                      />
                    </div>
                  </div>

                  {/* Low Warning */}
                  {isLow && (
                    <div className="mt-3 flex items-center gap-2 text-sm text-destructive">
                      <AlertTriangle className="h-4 w-4" />
                      Pocos folios restantes
                    </div>
                  )}

                  {/* Metadata */}
                  <div className="mt-3 text-xs text-muted-foreground">
                    <p>Folio actual: {folio.folioCurrent}</p>
                    <p>Autorizado: {new Date(folio.authorizedAt).toLocaleDateString()}</p>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Upload Dialog */}
      <CafUploadDialog
        open={uploadDialogOpen}
        onOpenChange={setUploadDialogOpen}
        onUpload={handleUploadComplete}
        isUploading={isUploading}
      />

      {/* Deactivate Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Desactivar Folio</AlertDialogTitle>
            <AlertDialogDescription>
              Esta acción desactivará el rango de folios seleccionado. Los folios
              no utilizados ya no estarán disponibles para emisión.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeactivateConfirm}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Desactivar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
